/**
 * ThemePicker Component
 * Allows users to select from preset accent color themes.
 */

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Palette, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export const themePresets = {
  coral: {
    name: "Coral",
    primary: "354 70% 49%",
    primaryForeground: "0 0% 100%",
  },
  blush: {
    name: "Blush",
    primary: "7 62% 47%",
    primaryForeground: "0 0% 100%",
  },
  peach: {
    name: "Peach",
    primary: "25 55% 40%",
    primaryForeground: "0 0% 100%",
  },
  sage: {
    name: "Sage",
    primary: "75 25% 36%",
    primaryForeground: "0 0% 100%",
  },
  moss: {
    name: "Moss",
    primary: "150 25% 37%",
    primaryForeground: "0 0% 100%",
  },
  teal: {
    name: "Teal",
    primary: "165 30% 36%",
    primaryForeground: "0 0% 100%",
  },
  ocean: {
    name: "Ocean",
    primary: "200 50% 39%",
    primaryForeground: "0 0% 100%",
  },
  lavender: {
    name: "Lavender",
    primary: "270 40% 53%",
    primaryForeground: "0 0% 100%",
  },
} as const;

export type ThemePreset = keyof typeof themePresets;

interface ThemePickerProps {
  currentTheme: ThemePreset;
  onThemeChange: (theme: ThemePreset) => void;
  disabled?: boolean;
}

export function ThemePicker({ currentTheme, onThemeChange, disabled }: ThemePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button aria-label="Theme" variant="outline" size="icon" disabled={disabled} className="h-11 w-11 opacity-70 hover:opacity-100" title="Theme">
          <Palette className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="end">
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">Choose Theme</h4>
          <div className="grid grid-cols-4 gap-2">
            {(Object.keys(themePresets) as ThemePreset[]).map((preset) => {
              const theme = themePresets[preset];
              const isSelected = currentTheme === preset;
              return (
                <button
                  key={preset}
                  onClick={() => onThemeChange(preset)}
                  className={cn(
                    "relative h-8 w-8 rounded-full border-2 transition-all hover:scale-110",
                    isSelected ? "border-foreground ring-2 ring-offset-2 ring-offset-background" : "border-transparent"
                  )}
                  style={{ backgroundColor: `hsl(${theme.primary})` }}
                  title={theme.name}
                >
                  {isSelected && (
                    <Check 
                      className="absolute inset-0 m-auto h-4 w-4" 
                      style={{ color: `hsl(${theme.primaryForeground})` }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {themePresets[currentTheme].name}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Helper to apply theme to document
export function applyTheme(preset: ThemePreset) {
  const theme = themePresets[preset];
  document.documentElement.style.setProperty("--primary", theme.primary);
  document.documentElement.style.setProperty("--primary-foreground", theme.primaryForeground);
  // Also update the teacher color to match the theme
  document.documentElement.style.setProperty("--teacher", theme.primary);
}
